import { keysToCamel, keysToSnake } from 'modules/common/helpers/transformKeysStyle';
import { BASE_URL, baseApi } from 'store/services/base';
import {
    CheckSharingCodeRequest,
    CheckSharingCodeResponse,
    CreateShareFolderUrlRequest,
    CreateShareFolderUrlResponse,
    IFolderShareRequest,
    ISharedEvidence,
    ISharedFolderDetails,
    IUnauthSharedFolder,
    SendSharingCodeRequest,
    SharingPriceRequest,
    SharingPriceResponse,
    ChatData,
    SendMessageRequest,
    ArchiveChatRequest,
    SendMessageResponse,
    SharingWithChats,
} from './types';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { RootState } from '../../index';
import {
    IOrganizationEvidenceBaseRequest,
    IOrganizationFolder,
    IOrganizationMultipartUploadCompleteRequest,
    IOrganizationMultipartUploadStartRequest,
    IOrganizationProofCreateRequest,
    IOrganizationUpdateProofRequest,
} from '../organizations/types';
import {
    CreateImageUploadLinkResponse,
    CreateImageUploadLinkResponseTransformed,
    MultipartUploadCompleteResponse,
    MultipartUploadCompleteResponseTransformed,
    MultipartUploadStartResponse,
    MultipartUploadStartResponseTransformed,
    ProofCreateResponse,
    ProofCreateResponseTransformed,
    UpdateProofResponse,
    UpdateProofResponseTransformed,
} from '../evidence/types';
import { setFulfilledFile, setPendingFile, setRejectedFile } from '../../slices/upload';

export const sharingApiAuth = createApi({
    reducerPath: 'sharingApiAuth',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL,
        prepareHeaders: (headers, { getState }) => {
            const {
                loginInfo: { accessFolderToken },
            } = getState() as RootState | any;

            if (accessFolderToken) {
                headers.set('authorization', `Bearer ${accessFolderToken}`);
            }

            return headers;
        },
    }),
    endpoints: (builder) => ({
        getAuthSharedFolder: builder.query<IUnauthSharedFolder, void>({
            query: () => ({ url: 'sharing/view/authenticated/' }),
            transformResponse: (res) => keysToCamel(res),
        }),
    }),
    tagTypes: [],
});

export const sharingApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getUnauthSharedFolder: builder.query<
            IUnauthSharedFolder & { accessToken: string },
            { cid?: string }
        >({
            query: (params) => ({ url: 'sharing/view/', params }),
            transformResponse: (res) => keysToCamel(res),
        }),

        createShareFolderUrl: builder.mutation<
            CreateShareFolderUrlResponse,
            CreateShareFolderUrlRequest
        >({
            query: ({ active, ...props }) => ({
                url: `sharing/url/create/`,
                method: 'POST',
                params: { active },
                body: keysToSnake(
                    Object.fromEntries(Object.entries(props).filter(([, val]) => !!val))
                ),
            }),
            transformResponse: (res) => keysToCamel(res),
        }),

        sendSharingCode: builder.mutation<{ id: string }, SendSharingCodeRequest>({
            query: (body) => ({
                url: `sharing/code/send/`,
                method: 'POST',
                body: keysToSnake(body),
            }),

            transformResponse: (res) => keysToCamel(res),
        }),

        checkSharingCode: builder.mutation<CheckSharingCodeResponse, CheckSharingCodeRequest>({
            query: ({ codeId, ...body }) => ({
                url: `sharing/code/${codeId}/check/`,
                method: 'POST',
                body: keysToSnake(body),
            }),

            transformResponse: (res) => keysToCamel(res),
        }),

        getSharingPrice: builder.query<SharingPriceResponse, SharingPriceRequest>({
            query: (body) => ({
                url: `/payments/sharing-price/calculate/`,
                method: 'POST',
                body: keysToSnake(body),
            }),

            transformResponse: (response) => keysToCamel(response),
        }),

        shareOrganizationFolder: builder.mutation<void, IFolderShareRequest>({
            query: ({ folderId, ...body }) => ({
                url: `sharing/folder/${folderId}/share/`,
                method: 'POST',
                body: keysToSnake(body),
            }),
        }),

        authenticateOrganizationFolder: builder.mutation<
            CheckSharingCodeResponse,
            { folderId: string; verificationCode: string }
        >({
            query: ({ folderId, ...body }) => ({
                url: `sharing/folder/${folderId}/authenticate/`,
                method: 'POST',
                body: keysToSnake(body),
            }),
        }),

        getOrganizationSharedFolderDetails: builder.query<
            ISharedFolderDetails,
            { folderId: string }
        >({
            query: ({ folderId }) => ({
                url: `/sharing/folder/${folderId}/details/`,
                method: 'GET',
            }),

            transformResponse: (response) => keysToCamel(response),
        }),

        getSharedFolderContents: builder.query<IOrganizationFolder, { folderId: string }>({
            query: ({ folderId }) => ({
                url: `sharing/folder/${folderId}/contents/`,
                method: 'GET',
            }),
        }),

        getOrganizationSharedEvidence: builder.query<
            ISharedEvidence,
            { folderId: string; stepId: string }
        >({
            query: ({ folderId, stepId }) => ({
                url: `sharing/evidence/${folderId}/${stepId}/`,
            }),
            transformResponse: (res) => keysToCamel(res),
            providesTags: ['OrganizationEvidence'],
        }),

        createAnonymousProof: builder.mutation<
            ProofCreateResponseTransformed,
            IOrganizationProofCreateRequest
        >({
            query({ folderId, stepId, ...payload }) {
                return {
                    url: `sharing/anonymous/proofs/${folderId}/${stepId}/create/`,
                    method: 'POST',
                    body: keysToSnake(
                        Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== ''))
                    ),
                };
            },

            transformResponse: (response: ProofCreateResponse) => keysToCamel(response),

            invalidatesTags: ['OrganizationEvidence'],
        }),

        anonymousMultipartUploadStart: builder.mutation<
            MultipartUploadStartResponseTransformed,
            IOrganizationMultipartUploadStartRequest
        >({
            query({ evidenceId, ...payload }) {
                return {
                    url: `sharing/anonymous/proofs/${evidenceId}/multipart-upload/start/`,
                    method: 'POST',
                    body: keysToSnake(payload),
                };
            },

            transformResponse: (response: MultipartUploadStartResponse) => {
                return {
                    uploadId: response.upload_id,
                    presignedUrls: Object.values(response.presigned_urls),
                };
            },

            async onQueryStarted({ evidenceId }, { dispatch, queryFulfilled }) {
                try {
                    dispatch(setPendingFile(evidenceId));
                    await queryFulfilled;
                } catch {
                    dispatch(setRejectedFile(evidenceId));
                }
            },

            invalidatesTags: ['OrganizationEvidence'],
        }),

        anonymousMultipartUploadComplete: builder.mutation<
            MultipartUploadCompleteResponseTransformed,
            IOrganizationMultipartUploadCompleteRequest
        >({
            query({ evidenceId, payload }) {
                return {
                    url: `sharing/anonymous/proofs/${evidenceId}/multipart-upload/finish/`,
                    method: 'POST',
                    body: keysToSnake(payload),
                };
            },

            transformResponse: (response: MultipartUploadCompleteResponse) => keysToCamel(response),

            async onQueryStarted({ evidenceId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(setFulfilledFile(evidenceId));
                } catch {
                    dispatch(setRejectedFile(evidenceId));
                }
            },

            invalidatesTags: ['OrganizationEvidence'],
        }),

        anonymousSingleUploadCreate: builder.mutation<
            CreateImageUploadLinkResponseTransformed,
            IOrganizationEvidenceBaseRequest
        >({
            query({ evidenceId }) {
                return {
                    url: `sharing/anonymous/proofs/proofs/${evidenceId}/single-upload-url/create/`,
                    method: 'POST',
                };
            },

            async onQueryStarted({ evidenceId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(setFulfilledFile(evidenceId));
                } catch {
                    dispatch(setRejectedFile(evidenceId));
                }
            },

            transformResponse: (response: CreateImageUploadLinkResponse) => {
                const { url, fields: requestHeaders } = response.presigned_upload_url;

                return {
                    url,
                    requestHeaders,
                };
            },
        }),

        updateAnonymousProof: builder.mutation<
            UpdateProofResponseTransformed,
            IOrganizationUpdateProofRequest
        >({
            query({ evidenceId, payload }) {
                return {
                    url: `sharing/anonymous/proofs/${evidenceId}/update/`,
                    method: 'PUT',
                    body: payload,
                };
            },

            async onQueryStarted({ evidenceId, uploaded }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    if (uploaded) {
                        dispatch(setFulfilledFile(evidenceId));
                    }
                } catch {
                    dispatch(setRejectedFile(evidenceId));
                }
            },

            transformResponse: (response: UpdateProofResponse) => keysToCamel(response),

            invalidatesTags: ['OrganizationEvidence'],
        }),

        getChat: builder.query<ChatData, string>({
            query: (sharingId) => ({
                url: `sharing/shared/${sharingId}/chat/`,
                method: 'GET',
            }),
            transformResponse: (res) => keysToCamel(res),
            providesTags: ['Chat'],
        }),

        sendMessage: builder.mutation<SendMessageResponse, SendMessageRequest>({
            query: ({ sharingId, content }) => ({
                url: `sharing/shared/${sharingId}/chat/messages/`,
                method: 'POST',
                body: keysToSnake({ content }),
            }),
            transformResponse: (res) => keysToCamel(res),
            invalidatesTags: ['Chat'],
        }),

        sendAnonymousMessage: builder.mutation<SendMessageResponse, SendMessageRequest>({
            query: ({ sharingId, content }) => ({
                url: `sharing/shared/${sharingId}/chat/messages/anonymous/`,
                method: 'POST',
                body: keysToSnake({ content }),
            }),
            transformResponse: (res) => keysToCamel(res),
            invalidatesTags: ['Chat'],
        }),

        archiveChat: builder.mutation<ChatData, ArchiveChatRequest>({
            query: ({ sharingId, confirm }) => ({
                url: `sharing/shared/${sharingId}/chat/archive/`,
                method: 'POST',
                body: keysToSnake({ confirm }),
            }),
            transformResponse: (res) => keysToCamel(res),
            invalidatesTags: ['Chat'],
        }),

        getSharingWithChats: builder.query<SharingWithChats[], void>({
            query: () => ({
                url: 'sharing/sharing-with-chats/',
                method: 'GET',
            }),
            transformResponse: (res) => keysToCamel(res),
            providesTags: ['Chat'],
        }),

        // FutureGram endpoints
        createScheduledShare: builder.mutation<
            any,
            {
                situationId: string;
                scheduledTime: string;
                recipients: Array<{ firstName: string; lastName: string; email: string }>;
                executors?: Array<{
                    firstName: string;
                    lastName: string;
                    email: string;
                    canAccessFiles: boolean;
                }>;
            }
        >({
            query: (body) => ({
                url: 'sharing/scheduled-shares/create/',
                method: 'POST',
                body: keysToSnake(body),
            }),
            transformResponse: (res) => keysToCamel(res),
        }),

        getFutureGramByToken: builder.query<any, { token: string }>({
            query: ({ token }) => ({
                url: `sharing/futuregram/view/${token}/`,
            }),
            transformResponse: (res) => keysToCamel(res),
        }),

        refreshFutureGramToken: builder.mutation<any, { token: string; email: string }>({
            query: ({ token, ...body }) => ({
                url: `sharing/futuregram/refresh-token/${token}/`,
                method: 'POST',
                body: keysToSnake(body),
            }),
            transformResponse: (res) => keysToCamel(res),
        }),

        getScheduledShareBySituation: builder.query<any, { situationId: string }>({
            query: ({ situationId }) => ({
                url: `sharing/futuregram/situation/${situationId}/`,
            }),
            transformResponse: (res) => keysToCamel(res),
            providesTags: ['ScheduledShare'],
        }),

        updateScheduledShareRecipients: builder.mutation<
            any,
            {
                shareId: string;
                recipients: Array<{ firstName: string; lastName: string; email: string }>;
            }
        >({
            query: ({ shareId, recipients }) => ({
                url: `sharing/futuregram/${shareId}/recipients/update/`,
                method: 'PUT',
                body: keysToSnake({ recipients }),
            }),
            transformResponse: (res) => keysToCamel(res),
            invalidatesTags: ['ScheduledShare'],
        }),

        updateScheduledShareExecutors: builder.mutation<
            any,
            {
                shareId: string;
                executors: Array<{
                    firstName: string;
                    lastName: string;
                    email: string;
                    canAccessFiles: boolean;
                }>;
            }
        >({
            query: ({ shareId, executors }) => ({
                url: `sharing/futuregram/${shareId}/executors/update/`,
                method: 'PUT',
                body: keysToSnake({ executors }),
            }),
            transformResponse: (res) => keysToCamel(res),
            invalidatesTags: ['ScheduledShare'],
        }),

        executorRespond: builder.mutation<
            any,
            { token: string; email: string; action: 'accepted' | 'declined' }
        >({
            query: ({ token, email, action }) => ({
                url: `sharing/futuregram/executor/respond/${token}/`,
                method: 'POST',
                body: keysToSnake({ email, action }),
            }),
            transformResponse: (res) => keysToCamel(res),
        }),

        resendExecutorInvitation: builder.mutation<any, { shareId: string; executorEmail: string }>(
            {
                query: ({ shareId, executorEmail }) => ({
                    url: `sharing/futuregram/${shareId}/executor/resend-invitation/`,
                    method: 'POST',
                    body: keysToSnake({ executor_email: executorEmail }),
                }),
                transformResponse: (res) => keysToCamel(res),
            }
        ),

        updateScheduledShareTime: builder.mutation<
            any,
            { shareId: string; scheduledTime: string; storageExpiryDate?: string }
        >({
            query: ({ shareId, scheduledTime, storageExpiryDate }) => ({
                url: `sharing/scheduled-shares/${shareId}/update/`,
                method: 'PUT',
                body: keysToSnake({
                    scheduled_time: scheduledTime,
                    ...(storageExpiryDate && { storage_expiry_date: storageExpiryDate }),
                }),
            }),
            transformResponse: (res) => keysToCamel(res),
            invalidatesTags: ['ScheduledShare'],
        }),

        calculateFutureGramExtensionPrice: builder.mutation<
            {
                totalPrice: number;
                proofkeepCharge: number;
                taxes: number;
                transactionFee: number;
                savedByWallet: number;
                requiresPayment: boolean;
                oldScheduledTime: string;
                newScheduledTime: string;
                daysExtended: number;
            },
            { shareId: string; newScheduledTime: string; newStorageExpiryDate?: string }
        >({
            query: (body) => ({
                url: 'sharing/futuregram/extension-price/calculate/',
                method: 'POST',
                body: keysToSnake(body),
            }),
            transformResponse: (res) => keysToCamel(res),
        }),
    }),

    overrideExisting: true,
});

export const {
    useGetUnauthSharedFolderQuery,
    useCreateShareFolderUrlMutation,
    useSendSharingCodeMutation,
    useCheckSharingCodeMutation,
    useGetSharingPriceQuery,
    useShareOrganizationFolderMutation,
    useGetOrganizationSharedFolderDetailsQuery,
    useGetSharedFolderContentsQuery,
    useAuthenticateOrganizationFolderMutation,
    useGetOrganizationSharedEvidenceQuery,
    useCreateAnonymousProofMutation,
    useAnonymousMultipartUploadStartMutation,
    useAnonymousMultipartUploadCompleteMutation,
    useAnonymousSingleUploadCreateMutation,
    useUpdateAnonymousProofMutation,
    useGetChatQuery,
    useSendMessageMutation,
    useArchiveChatMutation,
    useGetSharingWithChatsQuery,
    useSendAnonymousMessageMutation,
    useCreateScheduledShareMutation,
    useGetFutureGramByTokenQuery,
    useRefreshFutureGramTokenMutation,
    useGetScheduledShareBySituationQuery,
    useUpdateScheduledShareRecipientsMutation,
    useUpdateScheduledShareExecutorsMutation,
    useExecutorRespondMutation,
    useResendExecutorInvitationMutation,
    useUpdateScheduledShareTimeMutation,
    useCalculateFutureGramExtensionPriceMutation,
} = sharingApi;

export const { useGetAuthSharedFolderQuery } = sharingApiAuth;
